import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { api } from '../App';

const STATES = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' ');
const pct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);

export default function UtilityRateExplorer() {
  const [state, setState] = useState('MA');
  const [city, setCity] = useState('');
  const [bill, setBill] = useState(180);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.get('/api/market/utility-rates', { params: { state: city && !state ? undefined : state, city: city || undefined, monthly_bill: bill } });
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Rate data unavailable'); }
    setBusy(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="utility-rate-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">Market Intelligence</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-text-primary">Utility Rate Increases</h1>
        <p className="text-sm text-text-muted mt-1 max-w-2xl">Residential electricity rate increases by state or city, from the U.S. EIA. Show any homeowner what waiting has cost them, and what the next 25 years look like.</p>
      </div>

      <form onSubmit={lookup} className="p-4 rounded-2xl border border-border bg-surface flex flex-wrap items-end gap-3" data-testid="rate-lookup-form">
        <div className="w-32"><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">State</label>
          <select value={state} onChange={e => setState(e.target.value)} className="input-field" data-testid="rate-state-select">
            <option value="">From city</option>{STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div className="w-56"><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">City or ZIP (optional)</label>
          <input value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Boston, MA" className="input-field" data-testid="rate-city-input" /></div>
        <div className="w-40"><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Monthly bill $</label>
          <input type="number" min="20" max="2000" value={bill} onChange={e => setBill(e.target.value)} className="input-field" data-testid="rate-bill-input" /></div>
        <button type="submit" disabled={busy} className="btn-primary" data-testid="rate-lookup-btn">
          {busy ? <i className="fa-solid fa-spinner fa-spin mr-2" /> : <i className="fa-solid fa-chart-line mr-2" />}Pull rate data</button>
      </form>

      {data && <RateResult data={data} />}
    </div>
  );
}

function RateResult({ data }) {
  const where = data.city ? `${data.city} (${data.state})` : data.state;
  const stats = [
    ['Current rate', `${data.rate_cents}¢/kWh`, where, 'text-text-primary', 'rate-current'],
    ['Last 12 mo increase', pct(data.yoy_increase_pct), 'vs the 12 months before', 'text-rose-600', 'rate-yoy'],
    ['5-year increase', pct(data.five_year_increase_pct), 'avg rate vs 5 yrs ago', 'text-rose-600', 'rate-5yr'],
    ['Avg yearly climb', data.cagr_pct != null ? `${data.cagr_pct}%/yr` : `${data.escalation_pct_used}%/yr`, data.cagr_pct != null ? 'measured over the series' : 'national average used', 'text-amber-600', 'rate-cagr'],
  ];
  return (
    <div className="space-y-4" data-testid="rate-result">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(([l, v, s, tone, tid]) => (
          <div key={tid} className="card-interactive p-4" data-testid={tid}>
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{l}</span>
            <p className={`font-heading text-2xl font-bold mt-1 tabular-nums ${tone}`}>{v}</p>
            <p className="text-xs text-text-muted mt-0.5">{s}</p>
          </div>
        ))}
      </div>

      {data.series?.length > 0 && (
        <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="rate-chart-card">
          <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">Residential $/kWh by month · {where}</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.series} margin={{ left: 0, right: 12, top: 8, bottom: 0 }}>
                <CartesianGrid stroke="rgba(148,163,184,.2)" vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} minTickGap={28} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={v => `$${v.toFixed(2)}`} axisLine={false} tickLine={false} width={56} domain={['auto', 'auto']} />
                <Tooltip formatter={v => [`$${Number(v).toFixed(4)}/kWh`, 'Rate']} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Line type="monotone" dataKey="rate" stroke="#f43f5e" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="rate-projection">
        <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">If nothing changes: ${data.monthly_bill}/mo bill at {data.escalation_pct_used}%/yr</p>
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
          {data.projected.map(p => (
            <div key={p.year} className="rounded-xl bg-surface-alt border border-border p-2.5 text-center">
              <p className="text-[10px] font-bold text-text-muted uppercase">{p.year === 0 ? 'Today' : `Yr ${p.year}`}</p>
              <p className="text-sm font-bold text-text-primary">${p.annual_bill.toLocaleString()}</p>
              <p className="text-[10px] text-text-muted">/yr</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-text-secondary mt-3" data-testid="rate-25yr-spend">25-year spend with the utility: <b className="text-rose-600">${data.twenty_five_year_spend.toLocaleString()}</b></p>
      </div>

      <p className="text-[11px] text-text-muted" data-testid="rate-source"><i className="fa-solid fa-database mr-1" />Source: {data.source}{data.note ? ` · ${data.note}` : ''}</p>
    </div>
  );
}
